/* ==========================================================================
   Segal — "The name is written" waiting state
   --------------------------------------------------------------------------
   During the ~30s generate, the customer's own name is drawn in gold script,
   stroke by stroke, OVER THE PRODUCT IMAGE - the place they are already
   looking, and the place the finished necklace will appear.

   2026-09-01, second cut. The first version rendered this inside the Generate
   block next to the button. Yaniv: "i wanted the gif to be on the picture, not
   in the bar." It now mounts as an overlay on `.product-gallery__main`.

   That move also removes a duplication the first cut created: the switcher has
   its own placeholder (`.segal-name-drawing`) that writes the same name over
   the same gallery. While this overlay is up that placeholder is hidden, and it
   is restored on teardown, so only one animation is ever on screen.

   WHY THIS IS ITS OWN FILE. It touches nothing. It watches for the
   `.snui--busy` class that name-necklace-ui.js already sets, renders an
   overlay, and tears itself down when busy clears. Deleting the one <script>
   line in snippets/name-necklace-switcher.liquid removes the feature and
   restores the previous behaviour exactly.

   BOTH FAMILIES. The animation is the typed name, so it is identical on plain
   gold and diamond pages; only the phase wording differs, keyed off
   window.SegalNameNecklace.family. Nothing here can affect which product gets
   created - it never touches fetch, the form, or the variant.

   POLLING, NOT OBSERVING. The theme replaces the whole product subtree on a
   product swap, which detaches any MutationObserver bound to it. A 250ms
   interval survives that; the existing UI already ticks at 300ms.

   2026-09-22, "Why yours will last". PLAIN FAMILY ONLY. Under the drawn name,
   five benefits fade in on a timeline from mount: header 1s, bullets 2/6/10/
   14/18s, all held. Desktop shows the full list; <=600px shows one bullet at
   a time in a fixed 300x84 slot (the last one holds). prefers-reduced-motion
   shows everything at once. Approved copy, ChatGPT design round v4. Diamond
   pages render exactly as before (no .snd--why class, no benefit markup).
   ========================================================================== */
(() => {
  'use strict';

  const GOLD     = '#b78b43';   // brand gold, same value the UI uses
  const INK_SOFT = 'rgba(53,78,87,.6)';
  const DRAW_MS  = 26000;       // measured generates run 28-40s; the stroke
                                // finishes near 80% and the fill holds.
  const VB_W = 600, VB_H = 240;
  const TICK = 250;

  const reduce = window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cfg = window.SegalNameNecklace || {};
  const IS_DIAMOND = cfg.family === 'diamond';

  const PHASES = IS_DIAMOND
    ? ['Drawing the letterforms', 'Setting the stones', 'Almost there']
    : ['Drawing the letterforms', 'Joining the strokes', 'Almost there'];

  /* Benefits (plain only). [lead, explanation]. Wording approved 2026-09-22;
     "over 1 mm" = Ruslan's STL wall thickness, 1.10-1.20 mm.                 */
  const WHY_TITLE = 'Why yours will last';
  const WHY = IS_DIAMOND ? [] : [
    ['Soldered loops.', 'The chain is fused to your name and can’t pull open.'],
    ['Over 1 mm thick.', 'Cast solid, not stamped thin. It won’t bend.'],
    ['Solid gold, through and through.', 'Not plated, not filled.'],
    ['Handmade in Los Angeles.', 'Cast and polished by our own goldsmiths.'],
    ['One of one.', 'Made from scratch for this name only.'],
  ];
  const WHY_HEAD_MS = 1000;
  const WHY_AT_MS   = [2000, 6000, 10000, 14000, 18000];

  /* ---------------------------------------------------------------- font --
     Great Vibes is the closest Google face to the script Segal actually casts.
     If it fails to load the stroke draw still reads correctly in the fallback
     cursive - the effect is the animation, not the typeface.                 */
  const FONT_STACK = "'Great Vibes','Snell Roundhand','Brush Script MT',cursive,serif";
  (function loadFont() {
    if (document.getElementById('segal-nn-script-font')) return;
    const l = document.createElement('link');
    l.id = 'segal-nn-script-font';
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
    document.head.appendChild(l);
  })();

  /* ----------------------------------------------------------------- css -- */
  (function injectCss() {
    if (document.getElementById('segal-nn-draw-css')) return;
    const s = document.createElement('style');
    s.id = 'segal-nn-draw-css';
    s.textContent = `
.snd{position:absolute;inset:0;z-index:6;pointer-events:none;
  background:radial-gradient(120% 90% at 50% 38%,#ffffff 0%,#fbf8f1 62%,#f5efe3 100%);
  display:flex;flex-direction:column;align-items:center;justify-content:center}
.snd__svg{display:block;width:82%;max-width:560px;height:auto}
.snd__phase{margin-top:10px;font-size:11.5px;letter-spacing:.14em;text-transform:uppercase;
  color:${INK_SOFT};text-align:center;padding:0 16px;min-height:15px}
.snd__ink{fill:none;stroke:${GOLD};stroke-width:1.5;stroke-linecap:round;stroke-linejoin:round}
.snd__fill{fill:${GOLD};opacity:0}
.snd--static .snd__fill{opacity:1}
.snd--why{overflow:hidden}
.snd--why .snd__svg{width:70%;max-width:425px}
.snd--why .snd__phase{margin-top:-14px}
.snd__why{width:100%;box-sizing:border-box;margin-top:34px;padding:0 20px;
  display:flex;flex-direction:column;align-items:center;text-align:left}
.snd__whyhead{display:flex;flex-direction:column;align-items:center;margin-bottom:22px}
.snd__hair{width:42px;height:1px;background:${GOLD};margin-bottom:11px}
.snd__whyt{font-size:12px;line-height:1;font-weight:500;color:${GOLD};letter-spacing:.16em;
  text-transform:uppercase;text-align:center}
.snd__list{max-width:440px;margin:0;padding:0}
.snd__item{display:grid;grid-template-columns:14px minmax(0,1fr);column-gap:9px;align-items:start;margin:0 0 16px}
.snd__item:last-child{margin-bottom:0}
.snd__dot{width:6px;height:6px;border-radius:50%;background:${GOLD};margin-top:7px;justify-self:center}
.snd__copy{font-size:13.5px;line-height:1.45;color:rgba(53,78,87,.72);letter-spacing:0}
.snd__lead{display:block;color:#354e57;font-size:14.5px;font-weight:700;line-height:1.35;margin-bottom:2px}
.snd__fade{opacity:0;transform:translateY(6px);transition:opacity .4s ease,transform .4s ease}
.snd__fade.is-on{opacity:1;transform:none}
.snd--still .snd__fade{transition:none}
@media (max-width:600px){
.snd--why .snd__svg{width:62%;max-width:none}
.snd--why .snd__phase{margin-top:10px;font-size:9.5px;min-height:12px}
.snd__why{margin-top:24px}
.snd__whyhead{margin-bottom:14px}
.snd__hair{margin-bottom:7px}
.snd__whyt{font-size:10px}
.snd__list{width:300px;max-width:300px;height:84px;position:relative}
.snd__list .snd__item{position:absolute;left:0;top:0;width:300px;margin:0;display:block;text-align:center}
.snd__list .snd__item.is-on{opacity:0;transform:translateY(6px)}
.snd__list .snd__item.is-cur{opacity:1;transform:none}
.snd__dot{display:block;margin:0 auto 7px}
.snd__copy{width:300px;margin:0 auto;line-height:1.4;text-align:center}
.snd__lead{font-size:15px;margin-bottom:3px}
.snd--still .snd__list{height:auto;width:320px;max-width:320px;position:static}
.snd--still .snd__list .snd__item{position:static;width:320px;margin-bottom:2px}
.snd--still .snd__dot{display:none}
.snd--still .snd__copy{width:320px;font-size:11.5px;line-height:1.22}
.snd--still .snd__lead{font-size:12px;margin-bottom:0}
}`;
    document.head.appendChild(s);
  })();

  /* --------------------------------------------------------------- build -- */
  const SVGNS = 'http://www.w3.org/2000/svg';

  function buildStage(name) {
    const wrap = document.createElement('div');
    wrap.className = 'snd';
    wrap.setAttribute('aria-hidden', 'true');   // the Generate block speaks for a11y

    const svg = document.createElementNS(SVGNS, 'svg');
    svg.setAttribute('class', 'snd__svg');
    svg.setAttribute('viewBox', `0 0 ${VB_W} ${VB_H}`);
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    const g = document.createElementNS(SVGNS, 'g');

    const mk = (cls) => {
      const t = document.createElementNS(SVGNS, 'text');
      t.setAttribute('class', cls);
      t.setAttribute('x', String(VB_W / 2));
      t.setAttribute('y', String(VB_H / 2));
      t.setAttribute('text-anchor', 'middle');
      t.setAttribute('font-family', FONT_STACK);
      t.setAttribute('font-size', '104');
      t.textContent = name;
      return t;
    };

    const ink = mk('snd__ink');
    const fill = mk('snd__fill');
    g.appendChild(ink);
    g.appendChild(fill);
    svg.appendChild(g);

    const phase = document.createElement('div');
    phase.className = 'snd__phase';
    phase.textContent = PHASES[0];

    wrap.appendChild(svg);
    wrap.appendChild(phase);

    let whyHead = null;
    const whyItems = [];
    if (WHY.length) {
      wrap.classList.add('snd--why');
      if (reduce) wrap.classList.add('snd--still');

      const why = document.createElement('div');
      why.className = 'snd__why';

      whyHead = document.createElement('div');
      whyHead.className = 'snd__whyhead snd__fade';
      const hair = document.createElement('div');
      hair.className = 'snd__hair';
      const title = document.createElement('div');
      title.className = 'snd__whyt';
      title.textContent = WHY_TITLE;
      whyHead.appendChild(hair);
      whyHead.appendChild(title);

      const list = document.createElement('div');
      list.className = 'snd__list';
      WHY.forEach(([lead, text]) => {
        const item = document.createElement('div');
        item.className = 'snd__item snd__fade';
        const dot = document.createElement('span');
        dot.className = 'snd__dot';
        const copy = document.createElement('div');
        copy.className = 'snd__copy';
        const b = document.createElement('span');
        b.className = 'snd__lead';
        b.textContent = lead;
        copy.appendChild(b);
        copy.appendChild(document.createTextNode(text));
        item.appendChild(dot);
        item.appendChild(copy);
        list.appendChild(item);
        whyItems.push(item);
      });

      why.appendChild(whyHead);
      why.appendChild(list);
      wrap.appendChild(why);
    }

    return { wrap, svg, g, ink, fill, phase, whyHead, whyItems };
  }

  /* Benefits timeline. Runs from mount (t=0 = busy), independent of the font
     wait, so the copy never waits on Google Fonts. On desktop every shown item
     stays (.is-on); on mobile only the current one (.is-cur) is visible.      */
  function startWhy(parts, timers) {
    if (!parts.whyHead) return;
    const items = parts.whyItems;
    const show = (i) => {
      items.forEach((el, j) => el.classList.toggle('is-cur', j === i));
      items[i].classList.add('is-on');
    };
    if (reduce) {
      parts.whyHead.classList.add('is-on');
      items.forEach((el) => el.classList.add('is-on', 'is-cur'));
      return;
    }
    timers.push(setTimeout(() => parts.whyHead.classList.add('is-on'), WHY_HEAD_MS));
    WHY_AT_MS.forEach((ms, i) => {
      if (items[i]) timers.push(setTimeout(() => show(i), ms));
    });
  }

  /* Scale the word to fit. Long names (up to the 9-character cap) would
     otherwise run off both edges.                                            */
  function fit(parts) {
    let bb;
    try { bb = parts.g.getBBox(); } catch (_) { return 1500; }
    if (!bb || !bb.width) return 1500;

    const padX = 30, padY = 20;
    const s = Math.min(1, (VB_W - padX * 2) / bb.width, (VB_H - padY * 2) / bb.height);
    const cx = bb.x + bb.width / 2;
    const cy = bb.y + bb.height / 2;
    parts.g.setAttribute(
      'transform',
      `translate(${VB_W / 2} ${VB_H / 2}) scale(${s.toFixed(4)}) translate(${-cx} ${-cy})`
    );

    // A script glyph's outline runs roughly 3-4x its bounding width, and there is
    // no getTotalLength() for <text>. The failure directions are NOT equal:
    //   under-estimate -> the dash pattern REPEATS and the word appears in
    //                     disconnected pieces at once. Looks broken.
    //   over-estimate  -> the stroke completes early and holds. Looks fine.
    // So over-estimate deliberately.
    return Math.max(700, Math.round(bb.width * 4.0));
  }

  /* WHY THE WEB ANIMATIONS API AND NOT CSS. The first cut passed the dash length
     and duration in as custom properties - `animation: sndDraw var(--snd-dur)`
     with `@keyframes{from{stroke-dashoffset:var(--snd-dash)}}`. Measured over a
     real 36s generate: stroke-dashoffset never moved. It sat at its start value
     for 27 seconds then snapped to 0, and the fill's calc() delay also failed to
     apply, so the finished word appeared immediately. element.animate() takes
     real numbers and cannot be lost to a shorthand parse.

     MEASURE ONLY AFTER THE WEBFONT IS IN, or getBBox() returns the fallback
     cursive's metrics. Hard 1500ms cap so a blocked Google Fonts cannot leave
     an empty panel. */
  function start(parts) {
    const timers = [];
    let started = false;

    startWhy(parts, timers);

    const go = () => {
      if (started || !parts.wrap.isConnected) return;
      started = true;

      const dash = fit(parts);
      parts.ink.style.strokeDasharray = String(dash);
      parts.ink.style.strokeDashoffset = String(dash);

      if (reduce || typeof parts.ink.animate !== 'function') {
        parts.wrap.classList.add('snd--static');
        parts.ink.style.strokeDashoffset = '0';
        return;
      }

      parts.ink.animate(
        [{ strokeDashoffset: dash }, { strokeDashoffset: 0 }],
        { duration: DRAW_MS, easing: 'linear', fill: 'forwards' }
      );
      parts.fill.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        { duration: 1400, delay: Math.round(DRAW_MS * 0.9), easing: 'ease-out', fill: 'forwards' }
      );

      timers.push(setTimeout(() => { parts.phase.textContent = PHASES[1]; }, DRAW_MS * 0.42));
      timers.push(setTimeout(() => { parts.phase.textContent = PHASES[2]; }, DRAW_MS * 0.78));
    };

    if (document.fonts && typeof document.fonts.load === 'function') {
      try { document.fonts.load('104px "Great Vibes"').then(go, go); }
      catch (_) { go(); }
      timers.push(setTimeout(go, 1500));
    } else {
      go();
    }

    return () => { timers.forEach(clearTimeout); };
  }

  /* ------------------------------------------------- switcher placeholder --
     The switcher writes the same name over the same gallery. Hide it while we
     are up so the customer never sees the name animated twice, and put it back
     exactly as found on teardown.                                            */
  function hideSwitcherDrawing() {
    const hidden = [];
    document.querySelectorAll('.segal-name-drawing').forEach((el) => {
      hidden.push([el, el.style.display]);
      el.style.display = 'none';
    });
    return hidden;
  }
  function restoreSwitcherDrawing(hidden) {
    (hidden || []).forEach(([el, prev]) => { el.style.display = prev || ''; });
  }

  /* ---------------------------------------------------------------- loop -- */
  let mounted = null;   // { parts, name, cancel, hidden }

  function teardown() {
    if (!mounted) return;
    if (mounted.cancel) mounted.cancel();
    restoreSwitcherDrawing(mounted.hidden);
    if (mounted.parts.wrap.parentNode) mounted.parts.wrap.parentNode.removeChild(mounted.parts.wrap);
    mounted = null;
  }

  function typedName(box) {
    const el = box.querySelector('.snui__in');
    return el ? String(el.value || '').trim() : '';
  }

  // The gallery column. `.product-gallery__main` already carries position-relative
  // in this theme, so an inset:0 overlay lands on the image exactly.
  function stageHost() {
    return document.querySelector('.product-gallery__main')
        || document.querySelector('.product-gallery__main_slider-wrapper')
        || null;
  }

  setInterval(() => {
    try {
      const box = document.querySelector('.snui');
      if (!box) { teardown(); return; }

      const busy = box.classList.contains('snui--busy');
      if (!busy) { teardown(); return; }

      const name = typedName(box);
      if (!name) { teardown(); return; }

      const host = stageHost();
      if (!host) { teardown(); return; }

      if (mounted && mounted.name === name && mounted.parts.wrap.isConnected) return;

      teardown();

      const parts = buildStage(name);
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(parts.wrap);

      const hidden = hideSwitcherDrawing();
      const cancel = start(parts);
      mounted = { parts, name, cancel, hidden };
    } catch (_) {
      // A waiting animation must never break the page it waits on.
    }
  }, TICK);
})();
